// ============================================================
// PhoneLink.tsx — the ONE way a stored phone number is shown in view mode.
//   • the number itself is the Call link — tel:+<international> (telHrefOf),
//     so it dials correctly from any handler (phone, Teams, WhatsApp Desktop);
//   • a small WhatsApp icon beside it opens THAT number's chat
//     (https://wa.me/<international>, whatsAppHrefOf) in a new tab;
//   • a number that can't be normalised (too short / letters) renders as
//     plain text — never a broken link. Empty values are the caller's job
//     (each site keeps its own "Add" / dash empty state).
// Same normaliser as the activity call strip (AddActivityModal), so every
// number in the app resolves the same way. Edit inputs are untouched.
// ============================================================
import React from 'react'
import { telHrefOf, whatsAppHrefOf } from '../../utils/phoneLinks'

interface PhoneLinkProps {
  /** The number exactly as stored/typed — displayed as-is. */
  value: string
  /** Classes for the number (link styling at each site: LINK, BIG, glance…). */
  className?: string
  /** Classes for the number when it isn't a valid number (no link). */
  plainClassName?: string
  /** Icon size variant: 'sm' for glance strips. */
  size?: 'sm' | 'md'
}

export const WhatsAppGlyph: React.FC<{ className?: string }> = ({ className = 'w-3 h-3' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M3 21l1.65-3.8A9 9 0 1 1 7.5 20.1L3 21" />
    <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0v1a5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
  </svg>
)

/** Number styling for the glance strips (quiet until hovered). */
export const PHONE_GLANCE_LINK = 'hover:text-casevo-brand focus:outline-none casevo-focus rounded-[2px]'

const WA_BTN =
  'shrink-0 inline-grid place-items-center rounded-full border border-casevo-border bg-casevo-surface ' +
  'text-casevo-text-soft hover:text-casevo-brand dark:hover:text-[#8fd6b0] hover:border-casevo-mid transition-colors ' +
  'focus:outline-none casevo-focus'

const PhoneLink: React.FC<PhoneLinkProps> = ({ value, className = '', plainClassName, size = 'md' }) => {
  const shown = value.trim()
  const tel = telHrefOf(shown)
  const wa = whatsAppHrefOf(shown)
  if (!tel || !wa) {
    return <span className={plainClassName ?? 'tabular-nums'} title="Not a callable number — check it in Edit">{shown}</span>
  }
  const btn = size === 'sm' ? 'w-[20px] h-[20px]' : 'w-[22px] h-[22px]'
  const ico = size === 'sm' ? 'w-[11px] h-[11px]' : 'w-3 h-3'
  return (
    <span className="inline-flex items-center gap-[7px] min-w-0 max-w-full align-middle">
      <a className={`${className} tabular-nums`} href={tel} title={`Call ${shown}`}>{shown}</a>
      <a
        className={`${WA_BTN} ${btn}`}
        href={wa}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`WhatsApp ${shown}`}
        title={`WhatsApp ${shown}`}
        onClick={(e) => e.stopPropagation()}
      >
        <WhatsAppGlyph className={ico} />
      </a>
    </span>
  )
}

export default PhoneLink
